/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import type { ResourceViewState, ViewConfig, ViewStateRule } from "./types";

export const LOAD_MORE_INCREMENT = 50;
export const DEFAULT_PAGE_SIZE = 20;

/**
 * An ordered array of rules that define how the `ResourceViewState` is determined.
 * The first rule whose `when` condition evaluates to `true` will set the current view state.
 * The order of rules in this array is critical as it defines their priority.
 */
export const VIEW_STATE_RULES: ViewStateRule[] = [
  {
    state: "searchHint",
    when: ({ isSearchPhraseToShort }) => isSearchPhraseToShort,
  },
  {
    state: "searchClearLoading",
    when: ({ isLoadingResourceStates, hasSearchPhrase, settledSearchPhrase }) =>
      isLoadingResourceStates &&
      !hasSearchPhrase &&
      (settledSearchPhrase?.length ?? 0) > 0,
  },
  {
    state: "searchLoading",
    when: ({
      isSearchActive,
      isSearchPending,
      isLoadingResourceStates,
      loadRequest,
    }) =>
      isSearchActive &&
      (isSearchPending || (isLoadingResourceStates && loadRequest === null)),
  },
  {
    state: "initialLoading",
    when: ({ hasFetchedResources, hasSearchPhrase }) =>
      !hasFetchedResources && !hasSearchPhrase,
  },
  {
    state: "content",
    when: ({
      isSearchActive,
      hasGroups,
      hasFetchedResources,
      hasSearchPhrase,
    }) =>
      (isSearchActive && hasGroups) ||
      hasGroups ||
      (hasFetchedResources && !hasSearchPhrase && hasGroups),
  },
  {
    state: "searchEmpty",
    when: ({ isSearchActive }) => isSearchActive,
  },
  {
    state: "noResources",
    when: ({ hasResources }) => !hasResources,
  },
];

/**
 * A mapping from each `ResourceViewState` to its base `ViewConfig`.
 * This configuration defines the default UI properties (e.g., `showHeader`, `showLoader`, `showLoadMore`)
 * for each state. These base settings can be further modified by `deriveViewConfig` based on dynamic conditions.
 */
export const BASE_VIEW_CONFIG: Record<ResourceViewState, ViewConfig> = {
  initialLoading: { showHeader: true, showLoader: true, showLoadMore: false },
  searchHint: { showHeader: true, showLoader: false, showLoadMore: false },
  searchLoading: { showHeader: true, showLoader: true, showLoadMore: false },
  searchClearLoading: {
    showHeader: true,
    showLoader: true,
    showLoadMore: false,
  },
  searchEmpty: { showHeader: true, showLoader: false, showLoadMore: false },
  content: { showHeader: true, showLoader: false, showLoadMore: true },
  noResources: { showHeader: false, showLoader: false, showLoadMore: false },
  waiting: { showHeader: false, showLoader: false, showLoadMore: false },
};
